import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import * as Yup from "yup";
import {
  ApiErrorClass,
  Exercise,
  PlanTemplate,
  WorkoutCategories,
} from "@readyfit-common/models";
import {
  parseFitnessWorkout,
  parseRunWorkout,
  parseSwimWorkout,
} from "@readyfit-common/utils";
import { LoadingButton } from "src/grading/components";
import RFModal from "src/grading/components/RFModal/RFModal";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { showSnackbar } from "src/store/reducers/navigation/navigationSlice";
import {
  selectSelectedDayNumber,
  selectSelectedWeek,
  selectSelectedWorkout,
  selectWorkoutModalOpen,
  setSelectedDayNumber,
  setSelectedWorkout,
  setWorkoutModalOpen,
  useAddPlanTemplateWeekWorkoutMutation,
  useCopyPlanTemplateWeekWorkoutMutation,
  useSaveToWorkoutLibraryMutation,
  useUpdatePlanTemplateWeekWorkoutMutation,
} from "src/store/reducers/training/trainingApiSlice";
import theme from "src/theme";
import AddFromLibraryForm from "../AddFromLibraryForm/AddFromLibraryForm";
import EditWorkoutForm from "../WorkoutForm/WorkoutForm";
import useStyles from "./EditPlanTemplateModal.styles";

interface EditPlanTemplateModalProps {
  trainingPlan: PlanTemplate;
}

export interface WorkoutFormValues {
  name: string;
  workoutCategoryId: number;
  workoutTypeId: number;
  distance?: number | null;
  estimatedTime?: string | null;
  description?: string;
  exercises?: Exercise[];
  date?: string;
  saveToWorkoutLibrary?: boolean;
  workoutLibraryId?: string;
  tabValue?: number;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().when("tabValue", ([tabValue], schema) => {
    return tabValue === 1
      ? schema.required("Name is required")
      : schema.notRequired();
  }),
  workoutCategoryId: Yup.number().when("tabValue", ([tabValue], schema) => {
    return tabValue === 1
      ? schema.required("Workout category is required")
      : schema.notRequired();
  }),
  workoutTypeId: Yup.number().when("tabValue", ([tabValue], schema) => {
    return tabValue === 1
      ? schema.required("Workout type is required")
      : schema.notRequired();
  }),
  distance: Yup.number().nullable(),
  estimatedTime: Yup.string().nullable(),
  description: Yup.string().notRequired(),
  saveToWorkoutLibrary: Yup.boolean().notRequired(),
});

const EditPlanTemplateModal = ({
  trainingPlan,
}: EditPlanTemplateModalProps) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const open = useAppSelector(selectWorkoutModalOpen);
  const selectedWorkout = useAppSelector(selectSelectedWorkout);
  const selectedWeek = useAppSelector(selectSelectedWeek);
  const [
    updatePlanTemplateWeekWorkout,
    { isLoading: updatePlanTemplateWeekWorkoutLoading },
  ] = useUpdatePlanTemplateWeekWorkoutMutation();
  const [
    addPlanTemplateWeekWorkout,
    { isLoading: addPlanTemplateWeekWorkoutLoading },
  ] = useAddPlanTemplateWeekWorkoutMutation();
  const [saveToWorkoutLibrary, { isLoading: saveToWorkoutLibraryLoading }] =
    useSaveToWorkoutLibraryMutation();
  const [
    copyPlanTemplateWeekWorkout,
    { isLoading: copyPlanTemplateWeekWorkoutLoading },
  ] = useCopyPlanTemplateWeekWorkoutMutation();
  const selectedDayNumber = useAppSelector(selectSelectedDayNumber);

  const isEdit = useMemo(() => !!selectedWorkout?.id, [selectedWorkout]);
  const [tabValue, setTabValue] = useState(isEdit ? 1 : 0);

  const handleClose = () => {
    formik.resetForm();
    dispatch(setWorkoutModalOpen(false));
    dispatch(setSelectedWorkout(null));
    dispatch(setSelectedDayNumber(null));
    setTabValue(0);
  };

  const initialValues: WorkoutFormValues = {
    name: selectedWorkout?.name || "",
    workoutCategoryId: selectedWorkout?.workoutCategoryId as number,
    workoutTypeId: selectedWorkout?.workoutTypeId as number,
    distance: selectedWorkout?.distance || null,
    estimatedTime: selectedWorkout?.estimatedTime || null,
    description: selectedWorkout?.description || "",
    exercises: selectedWorkout?.exercises || [],
    saveToWorkoutLibrary: false,
    tabValue: tabValue,
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: async (values: WorkoutFormValues) => {
      let response;
      try {
        if (tabValue === 0) {
          if (!values.workoutLibraryId) {
            throw new ApiErrorClass({
              message: "No Workout Selected",
            });
          }
          if (!selectedWeek?.id) {
            throw new ApiErrorClass({
              message: "No week id",
            });
          }
          if (!selectedDayNumber) {
            throw new ApiErrorClass({
              message: "No day number selected",
            });
          }
          response = await copyPlanTemplateWeekWorkout({
            planTemplateId: trainingPlan.id,
            phaseId: selectedWeek.phaseId,
            weekId: selectedWeek.id,
            dayNumber: selectedDayNumber,
            workoutLibraryId: values.workoutLibraryId,
          }).unwrap();
          if (!response?.success)
            throw new ApiErrorClass({
              message: "Error copying workout",
            });
          dispatch(
            showSnackbar({
              open: true,
              message: "Workout copied successfully",
            }),
          );
          handleClose();
          return;
        }

        const workoutPayload = () => {
          if (!selectedDayNumber) {
            throw new ApiErrorClass({
              message: "No day number selected",
            });
          }

          const date = selectedWorkout?.date || "";

          switch (values.workoutCategoryId) {
            case WorkoutCategories.RUN:
              return parseRunWorkout(values, date);
            case WorkoutCategories.SWIM:
              return parseSwimWorkout(values, date);
            case WorkoutCategories.FITNESS:
              return parseFitnessWorkout(values, date);
            default:
              return values;
          }
        };
        if (!selectedDayNumber) {
          throw new ApiErrorClass({
            message: "No day number selected",
          });
        }
        // EDIT WORKOUT
        if (isEdit) {
          if (!selectedWorkout?.id) {
            throw new ApiErrorClass({
              message: "No workout id",
            });
          }
          if (!selectedWeek?.id) {
            throw new ApiErrorClass({
              message: "No week id",
            });
          }

          response = await updatePlanTemplateWeekWorkout({
            planTemplateId: trainingPlan.id,
            phaseId: selectedWeek.phaseId,
            weekId: selectedWeek.id,
            workoutId: selectedWorkout.id,
            workout: workoutPayload(),
            dayNumber: selectedDayNumber,
          }).unwrap();
        } else {
          // ADD WORKOUT
          if (!selectedWeek?.id) {
            throw new ApiErrorClass({
              message: "No week id",
            });
          }

          response = await addPlanTemplateWeekWorkout({
            planTemplateId: trainingPlan.id,
            phaseId: selectedWeek.phaseId,
            weekId: selectedWeek.id,
            workout: workoutPayload(),
            dayNumber: selectedDayNumber,
          }).unwrap();
        }

        if (values.saveToWorkoutLibrary) {
          await saveToWorkoutLibrary(workoutPayload()).unwrap();
        }

        if (!response?.success)
          throw new Error(response?.message || "Error saving workout");

        dispatch(
          showSnackbar({
            open: true,
            message: "Workout updated successfully",
          }),
        );

        handleClose();
      } catch (error: unknown) {
        const typedError = error as ApiErrorClass;
        console.log("error", error);
        dispatch(
          showSnackbar({
            open: true,
            message: typedError?.data?.message || "Error updating workout",
            type: "error",
          }),
        );
      }
    },
  });

  const {
    values,
    setFieldValue,
    handleSubmit,
    errors,
    handleChange,
    resetForm,
  } = formik;

  const renderTabContent = () => {
    switch (tabValue) {
      case 0:
        return (
          <AddFromLibraryForm
            values={values}
            errors={errors}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            resetForm={resetForm}
          />
        );
      case 1:
        return (
          <EditWorkoutForm
            values={values}
            errors={errors}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
          />
        );
      // return <AddWorkoutForm />;
      default:
        return null;
    }
  };

  return (
    <RFModal
      open={open}
      boxProps={{ className: classes.container }}
      onClose={handleClose}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Grid
        container
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit();
        }}
        rowGap={theme.spacing(3)}
      >
        <Typography variant="h5" fontSize={18}>
          {isEdit ? "Edit Workout" : "Add Workout"}
        </Typography>

        {/* TABS */}
        <Grid container justifyContent="center">
          <Tabs
            value={tabValue}
            onChange={(event, newValue) => {
              setTabValue(newValue);
              setFieldValue("tabValue", newValue);
            }}
            className={classes.tabs}
            TabIndicatorProps={{
              style: { display: "none" },
            }}
          >
            <Tab className={classes.tab} label="Add from library" />
            <Tab
              className={classes.tab}
              label={isEdit ? "Edit workout" : "Add workout"}
            />
          </Tabs>
        </Grid>

        {/* TAB CONTENT */}
        {renderTabContent()}

        <Grid
          container
          item
          xs={12}
          justifyContent="space-between"
          marginTop={theme.spacing(4)}
        >
          {tabValue === 1 && (
            <Grid container>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.saveToWorkoutLibrary}
                    onChange={(e) =>
                      setFieldValue("saveToWorkoutLibrary", e.target.checked)
                    }
                  />
                }
                label="Save to workout library"
              />
            </Grid>
          )}

          <Grid item>
            <Button
              color="secondary"
              className={` ${classes.button} ${classes.backButton}`}
              onClick={handleClose}
            >
              Cancel
            </Button>
          </Grid>

          <Grid item>
            <LoadingButton
              variant="contained"
              color="primary"
              className={classes.button}
              label="Save"
              onClick={() => handleSubmit()}
              loading={
                updatePlanTemplateWeekWorkoutLoading ||
                addPlanTemplateWeekWorkoutLoading ||
                saveToWorkoutLibraryLoading ||
                copyPlanTemplateWeekWorkoutLoading
              }
            />
          </Grid>
        </Grid>
      </Grid>
    </RFModal>
  );
};

export default EditPlanTemplateModal;
